import { Link } from "react-router";
import { RouteFactory } from "@app/routePaths";
import Ad from "@components/Advertisement/Ad";
import Breadcrumb from "@components/Breadcrumb/Breadcrumb";
import { ActiveType } from "@components/Breadcrumb/types";
import Container from "@components/Container/Container";
import SeoFooter from "@components/Footer/SeoFooter/SeoFooter";
import { GADataProps } from "@components/GAData/GAData";
import Column from "@components/Grid/Column";
import Row from "@components/Grid/Row";
import Header from "@components/Header/Header";
import { useBreadCrumbState } from "@components/Header/helpers";
import { useInjectClientData } from "@hooks/useInjectClientData";
import { useLoadMoreSupplementStories } from "@hooks/useLoadMoreSupplementStories";
import { useTrimmedBreakingNews } from "@hooks/useTrimmedBreakingNews";
import BreakingNews from "@routes/Section/components/BreakingNews/BreakingNews";
import SupplementsGroup from "@routes/Section/layouts/Supplements/components/SupplementsGroup";
import { SupplementsPageData } from "@routes/Section/layouts/Supplements/utils/types";
import { sectionNavigationItems } from "@util/constant";
import { cn } from "@util/helpers";

const WealthInsurancePageContent = ({
  group,
  seoFooterData,
  gaProps,
}: SupplementsPageData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const breakingNews = useTrimmedBreakingNews();
  const { breadcrumbsRef, isShowBreadCrumb } = useBreadCrumbState();
  const { moreGroup, ref } = useLoadMoreSupplementStories({
    group,
    size: 10,
    section: "wealth",
  });
  const childCategory = "insurance";

  return (
    <div
      className="w-full"
      data-testid="wealth-supplements-page-content-component"
    >
      <Ad
        adType="lb1"
        className="flex min-h-[calc(50px+0.50rem)] flex-col items-center justify-center border-b border-gray-175 bg-gray-125 py-1 text-center md:py-3 lg:min-h-[calc(250px+1.5rem)]"
      />

      <Header
        displayBreadcrumb={sectionNavigationItems["wealth"] && isShowBreadCrumb}
        parentCategory={"wealth"}
        categoryDataMapping={sectionNavigationItems}
        childCategory={childCategory}
      />

      <Container>
        <div>
          <h1 className="pb-3 pt-6 font-playfair text-8xl font-semibold uppercase text-gray-850">
            {"Wealth"}
          </h1>

          <div ref={breadcrumbsRef}>
            <Breadcrumb
              className="m-auto"
              parentCategory={"wealth"}
              childCategory={childCategory}
              activeFilter={RouteFactory.wealthInsurance.replace("/", "")}
              categoryDataMapping={sectionNavigationItems}
              activeType={ActiveType.QUERY}
              extraBreadcrumbItems={
                <div
                  className={cn(
                    "relative box-border flex shrink-0 items-center border-r border-gray-175 pr-3 font-poppins text-sm tracking-tightest text-gray-850 hover:opacity-80"
                  )}
                >
                  <Link
                    to={`${RouteFactory.wealth}?ref=listing-menubar`}
                    reloadDocument
                  >
                    Top stories
                  </Link>
                </div>
              }
            />
          </div>
        </div>
      </Container>

      <Container rootClassName="py-12">
        <Row>
          <Column rootClassName="w-full mb-8 lg:w-8/12">
            <div className="mb-8 grid grid-cols-1 gap-8 lg:grid-cols-2">
              {moreGroup.map(({ entity, articles }, index) => {
                if (entity === null) return;

                return (
                  <SupplementsGroup
                    key={entity.urlPath}
                    tag={entity}
                    articlesProps={articles}
                    index={index}
                    className={cn("flex flex-col", {
                      "col-span-1 lg:col-span-2": index === 0,
                    })}
                  />
                );
              })}
            </div>

            <div ref={ref} />
          </Column>

          <Column rootClassName="w-full lg:w-4/12">
            <Ad
              adType="imu2"
              className="mb-6 flex flex-col items-center justify-center"
            />

            <BreakingNews
              data={breakingNews}
              rootClassName="max-w-[300px] mx-auto mb-6"
            />
          </Column>
        </Row>
      </Container>

      <SeoFooter data={seoFooterData} />

      <Ad adType="catfish" />

      <Ad adType="prestitial" />
    </div>
  );
};

export default WealthInsurancePageContent;
